import Seo from "../components/Seo";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Bell, BellRing, Eye, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import PortfolioSparkline from "../components/PortfolioSparkline";
import ConstellationBackground from "../components/ConstellationBackground";
import PortfoliosHeroMobile from "../components/PortfoliosHeroMobile";
import AdminReaderPreview, { PORTFOLIO_VIEWS, readerViewLabel } from "../components/AdminReaderPreview";
import TierPill from "../components/TierBadge";
import { tierStyle } from "../lib/tiers";
import { TIER_LABEL, colorAt, fmtPctFr, fmtNumFr, fmtMonthFr } from "../lib/portfolioMeta";

// Prix des offres, lus depuis /config (ex. « 19,99 €/mois »)
function useTierPrice() {
  const { data: cfg } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  return (tier) => {
    const entry = cfg?.tiers?.[tier];
    return entry ? `${entry.price_eur.toString().replace(".", ",")} €/mois` : null;
  };
}

// Pastille d'offre aux couleurs du site (Gratuit gris, Essentiel orange, Pro vert, Max violet)
function TierBadge({ tier, locked }) {
  const t = tier || "free";
  return <TierPill tier={t} locked={!!locked && t !== "free"} />;
}

function AllocationBar({ allocation = [], locked, tier }) {
  if (!allocation.length) {
    return <div className="text-[11px] text-zinc-600">Pas de positions ouvertes</div>;
  }
  return (
    <>
      <div className="flex h-2 rounded-full overflow-hidden gap-px">
        {allocation.map((a, i) => (
          <div key={i} style={{ width: `${a.weight_pct}%`, background: colorAt(i) }} />
        ))}
      </div>
      {locked ? (
        <div className={`mt-2 flex items-center gap-1.5 text-xs ${tierStyle(tier).text}`}>
          <Lock className="w-3.5 h-3.5" /> Composition réservée à l'offre {TIER_LABEL[tier] || tier}
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {allocation.slice(0, 4).map((a, i) => (
            <span key={a.symbol} className="inline-flex items-center gap-1.5 text-zinc-300">
              <span className="w-2 h-2 rounded-full" style={{ background: colorAt(i) }} />
              {a.symbol} <span className="text-zinc-500 font-mono-num">{fmtNumFr(a.weight_pct, 0)} %</span>
            </span>
          ))}
          {allocation.length > 4 && <span className="text-zinc-600">+{allocation.length - 4}</span>}
        </div>
      )}
    </>
  );
}

/**
 * Cloche « suivre » d'une carte : verte et pleine = suivi, grise = pas suivi. Un clic suit / arrête de suivre
 * sans ouvrir la carte. Visiteur non connecté → page de connexion. Pendant l'aperçu admin : désactivée.
 */
function FollowBell({ p, loggedIn, isPreviewing }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [following, setFollowing] = useState(!!p.is_following);
  useEffect(() => setFollowing(!!p.is_following), [p.is_following]);
  const mut = useMutation({
    mutationFn: async (next) =>
      (next ? await api.post(`/sideb/portfolios/${p.id}/follow`) : await api.delete(`/sideb/portfolios/${p.id}/follow`)).data,
    onMutate: (next) => setFollowing(next),
    onSuccess: (r) => {
      toast.success(r.following ? "Vous suivez ce portefeuille : alerte à chaque mouvement" : "Vous ne suivez plus ce portefeuille");
      qc.invalidateQueries({ queryKey: ["sideb-portfolios"] });
      qc.invalidateQueries({ queryKey: ["sideb-portfolio", p.slug] });
      qc.invalidateQueries({ queryKey: ["sideb-following"] });
    },
    onError: (e, next) => {
      setFollowing(!next);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    },
  });
  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPreviewing) return toast.info("Bouton désactivé pendant l'aperçu");
    if (!loggedIn) return navigate("/login");
    if (!mut.isPending) mut.mutate(!following);
  };
  const on = loggedIn && following;
  return (
    <button
      type="button"
      onClick={onClick}
      title={on ? "Ne plus suivre" : "Suivre : alerte à chaque mouvement"}
      aria-label={on ? `Ne plus suivre ${p.name}` : `Suivre ${p.name}`}
      aria-pressed={on}
      className={`grid place-items-center w-9 h-9 rounded-full border transition-colors ${
        on
          ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"
          : "border-white/10 text-zinc-500 hover:text-white hover:border-white/25"
      }`}
      data-testid={`sideb-bell-${p.slug}`}
    >
      {on ? <BellRing className="w-4 h-4" fill="currentColor" /> : <Bell className="w-4 h-4" />}
    </button>
  );
}
function PortfolioCard({ p, viewerTier, price, loggedIn, isPreviewing }) {
  const m = p.metrics || {};
  const up = (m.total_return_pct ?? 0) >= 0;
  const locked = !!p.is_locked;
  const tierName = TIER_LABEL[p.tier_required] || p.tier_required;
  const isUpgrade = viewerTier && viewerTier !== "free";

  return (
    <Link to={`/portefeuilles-sideb/${p.slug}`} className="block h-full min-w-0" data-testid={`sideb-portfolio-card-${p.slug}`}>
      <Card
        className={`group p-6 bg-card border-white/5 h-full flex flex-col gap-5 transition-all ${
          locked ? tierStyle(p.tier_required).hover : "hover:border-emerald-500/30"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-xl group-hover:text-emerald-300 transition-colors">{p.name}</h3>
            {p.description && <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{p.description}</p>}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-2">
              {!locked && <FollowBell p={p} loggedIn={loggedIn} isPreviewing={isPreviewing} />}
              <TierBadge tier={p.tier_required} locked={locked} />
            </div>
            {p.published === false && (
              <span className="text-[10px] uppercase tracking-[0.15em] text-amber-400">Non publié</span>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-baseline gap-2">
            <span className={`font-display font-black text-3xl ${up ? "text-emerald-400" : "text-rose-400"}`}>
              {fmtPctFr(m.total_return_pct)}
            </span>
            <span className="text-xs text-zinc-500">depuis le lancement</span>
          </div>
          <div className="mt-2 -mx-1">
            <PortfolioSparkline points={p.spark} up={up} />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Annualisé</div>
            <div className={`mt-1 font-mono-num ${m.cagr_pct == null ? "text-zinc-500" : "text-zinc-200"}`}>
              {m.cagr_pct == null ? "—" : fmtPctFr(m.cagr_pct)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pire baisse</div>
            <div className="mt-1 font-mono-num text-rose-300">−{fmtNumFr(m.max_drawdown_pct ?? 0)} %</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Volatilité</div>
            <div className="mt-1 font-mono-num text-zinc-200">{fmtNumFr(m.volatility_30d_pct ?? 0)} %</div>
          </div>
        </div>

        <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
          <AllocationBar allocation={p.allocation} locked={locked} tier={p.tier_required} />
        </div>

        <div className="mt-auto pt-1 space-y-3">
          <div className="flex items-center justify-between gap-3 text-xs text-zinc-500">
            <span>
              {m.start_date ? `Lancé en ${fmtMonthFr(m.start_date)} · ` : ""}
              {p.followers_count ?? 0}{"\u00a0"}suivent
            </span>
            {!locked && (
              <span className="shrink-0 whitespace-nowrap inline-flex items-center gap-1 text-emerald-400 font-medium group-hover:gap-2 transition-all">
                Voir le portefeuille <ArrowRight className="w-3.5 h-3.5" />
              </span>
            )}
          </div>
          {locked && (
            <span
              className={`w-full inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium h-9 transition-colors ${tierStyle(p.tier_required).cta}`}
              data-testid={`sideb-upgrade-cta-${p.slug}`}
            >
              {isUpgrade ? `Passer à ${tierName}` : "Débloquer"}
              {price ? ` · ${price}` : ""} <ArrowRight className="w-4 h-4" />
            </span>
          )}
        </div>
      </Card>
    </Link>
  );
}

export default function SideBPortfoliosPage() {
  const { user } = useAuth();
  const isAdmin = !!(user && user.role === "admin");

  // Aperçu admin : simule un visiteur, un membre gratuit ou un abonné (Essentiel, Pro, Max)
  const [previewAs, setPreviewAs] = useState("admin");
  const isPreviewing = isAdmin && previewAs !== "admin";
  const previewParam = isPreviewing ? previewAs : null;
  const viewerTier = isPreviewing
    ? ["essentiel", "pro", "max"].includes(previewAs)
      ? previewAs
      : "free"
    : user?.subscription_tier || "free";

  const priceOf = useTierPrice();
  const { data: portfolios = [], isLoading } = useQuery({
    queryKey: ["sideb-portfolios", previewParam],
    queryFn: async () =>
      (await api.get("/sideb/portfolios", { params: previewParam ? { preview_as: previewParam } : undefined })).data,
  });

  return (
    <div className="min-h-screen">
      <Seo title="Portefeuilles Side B" description="Les portefeuilles Side B suivis en direct : performance, transactions et stratégie expliquée." path="/portefeuilles-sideb" />
      {isPreviewing && (
        <div className="border-b border-violet-500/20 bg-violet-500/[0.08]" data-testid="admin-preview-banner">
          <div className="mx-auto max-w-7xl px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="text-violet-200 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> Aperçu admin : vous voyez les portefeuilles comme
              <b className="font-semibold text-white">{readerViewLabel(previewAs, PORTFOLIO_VIEWS)}</b>
            </span>
            <button
              type="button"
              onClick={() => setPreviewAs("admin")}
              className="text-violet-300 hover:text-white underline-offset-4 hover:underline"
            >
              Revenir à ma vue
            </button>
          </div>
        </div>
      )}

      <section className="relative border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-40" />
        {/* Ordinateur : constellation d'actifs ; téléphone : animation propre (PortfoliosHeroMobile) */}
        <ConstellationBackground className="max-sm:hidden" />
        <PortfoliosHeroMobile />
        <div className="relative mx-auto max-w-7xl px-6 py-8 sm:py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-2 sm:mb-3">Portefeuilles Side B</div>
          {/* Téléphone : le titre tient sur une ligne (taille adaptée à la largeur de l'écran) */}
          <h1 className="font-display font-black text-[length:clamp(1.15rem,6.1vw,2rem)] max-sm:whitespace-nowrap leading-tight sm:text-5xl">Portefeuilles suivis en direct</h1>
          <p className="mt-2 sm:mt-4 text-[15px] sm:text-base text-zinc-400 max-w-2xl">
            Nos portefeuilles réels, mis à jour à chaque achat ou vente. Suivez-en un pour être prévenu et comprendre
            nos choix dans la durée.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pt-8 pb-8 sm:py-12">
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-[30rem] rounded-xl bg-white/[0.02] border border-white/5 animate-pulse" />
            ))}
          </div>
        ) : portfolios.length === 0 ? (
          <div className="text-center py-24 text-zinc-500">Aucun portefeuille publié pour l'instant.</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="sideb-portfolios-grid">
            {/* grid-cols-1 : sur iPhone (Safari), la courbe des cartes élargissait la colonne au-delà de l'écran */}
            {portfolios.map((p) => (
              <PortfolioCard
                key={p.id}
                p={p}
                viewerTier={viewerTier}
                price={priceOf(p.tier_required)}
                loggedIn={isPreviewing ? previewAs !== "guest" : !!user}
                isPreviewing={isPreviewing}
              />
            ))}
          </div>
        )}
      </section>

      {isAdmin && <AdminReaderPreview value={previewAs} onChange={setPreviewAs} views={PORTFOLIO_VIEWS} />}
    </div>
  );
}
